import { useState } from 'react';
import { CheckCircle2, CircleAlert, Link2, MessageSquareOff, PlugZap, RefreshCw, Satellite, Stethoscope, Truck } from 'lucide-react';
import { cn } from '@/lib/cn';
import { formatAgo } from '@/lib/format';
import {
  useLinkVehicles,
  usePollNow,
  useProviderVehicles,
  useProviders,
  useSaveProvider,
  useTestProvider,
  type LinkReport,
  type ProviderTestResult,
} from '@/api/hooks/integrations';
import { Badge } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import { Card, CardHeader } from '@/components/ui/Card';
import { Field, Input } from '@/components/ui/Field';
import { FrotcomDiagnoseDialog } from '@/components/integrations/FrotcomDiagnoseDialog';
import { ImportVehiclesDialog } from '@/components/integrations/ImportVehiclesDialog';
import { ErrorState, LoadingBlock } from '@/components/ui/State';
import { toast } from '@/stores/toast';

/**
 * ═══════════════════════════════════════════════════════════════════════════
 *  ВРЪЗКИ С ДРУГИ СИСТЕМИ.
 *
 *  Frotcom: точните позиции, километри и гориво на бусовете. Достъпът до API
 *  се дава от партньора на Frotcom (за България — Frotcom Bulgaria, Пловдив) и
 *  е ОТДЕЛЕН потребител — не входът в сайта на Frotcom. Паролата се пази
 *  криптирана и никога не се показва обратно.
 *
 *  Trans.eu: проверено какво позволява публичният им API — тук е казано
 *  честно, включително какво НЕ може (чатовете).
 * ═══════════════════════════════════════════════════════════════════════════
 */

export function IntegrationsPage() {
  const providers = useProviders();
  const frotcom = providers.data?.find((p) => p.adapter === 'frotcom' && p.isActive) ?? null;

  return (
    <div className="flex min-h-full flex-col gap-4 p-3 sm:p-5">
      <div>
        <h1 className="font-display text-xl font-bold tracking-tight">Frotcom и връзки</h1>
        <p className="text-sm text-ink-dim">GPS на бусовете и връзки с други системи.</p>
      </div>

      {providers.isLoading ? (
        <LoadingBlock />
      ) : providers.isError ? (
        <ErrorState onRetry={() => void providers.refetch()} />
      ) : (
        <FrotcomCard provider={frotcom} />
      )}

      <TransEuCard />
    </div>
  );
}

function FrotcomCard({ provider }: { provider: { id: string; lastSyncAt: string | null; lastError: string | null; lastErrorAt: string | null; configuredKeys: Record<string, string> } | null }) {
  const test = useTestProvider();
  const save = useSaveProvider();
  const link = useLinkVehicles();
  const poll = usePollNow();
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [result, setResult] = useState<ProviderTestResult | null>(null);
  const [report, setReport] = useState<LinkReport | null>(null);
  const [editing, setEditing] = useState(provider === null);
  const [diagnosing, setDiagnosing] = useState(false);
  /** Записаният доставчик — и в мига след първото записване, преди списъкът да се опресни. */
  const [savedId, setSavedId] = useState<string | null>(null);
  const providerId = provider?.id ?? savedId;
  const [importing, setImporting] = useState(false);
  /** Щом е свързан, сравнението с нашите бусове се вижда веднага — и бутонът за внасяне. */
  const status = useProviderVehicles(provider !== null ? providerId : null);
  const shown = report ?? status.data ?? null;

  const connected = provider !== null;
  const failing = connected && provider.lastErrorAt !== null && (!provider.lastSyncAt || provider.lastErrorAt > provider.lastSyncAt);

  async function saveAndLink() {
    const body = { adapter: 'frotcom', label: 'Frotcom', config: { username: username.trim(), password }, isActive: true };
    const saved = await save.mutateAsync(provider ? { id: provider.id, ...body } : body);
    setSavedId(saved.id);
    const linked = await link.mutateAsync(saved.id);
    setReport(linked);
    setEditing(false);
    setPassword('');
    toast.ok(`Frotcom е свързан`, `${linked.linked.length} от ${linked.linked.length + linked.unmatchedOurs.length} буса сдвоени по регистрационен номер.`);
  }

  return (
    <Card>
      <CardHeader
        title={
          <span className="inline-flex items-center gap-2">
            <Satellite className="size-4 text-accent" />
            Frotcom — точните позиции, километри и гориво
          </span>
        }
        subtitle="Бусовете на картата и в „Начало“ идват оттук. Километрите влизат в справката за горивото."
        actions={
          connected ? (
            failing ? (
              <Badge tone="danger">
                <CircleAlert className="size-3.5" /> грешка
              </Badge>
            ) : (
              <Badge tone="ok">
                <CheckCircle2 className="size-3.5" /> свързан
              </Badge>
            )
          ) : (
            <Badge tone="warn">не е свързан</Badge>
          )
        }
      />
      <div className="grid gap-6 p-4 lg:grid-cols-[1.1fr_1fr]">
        <div>
          <h3 className="mb-2 text-sm font-semibold">Как се взима достъп до API (веднъж)</h3>
          <ol className="space-y-3 text-sm text-ink-dim">
            <Step n={1}>
              Свържете се с партньора на Frotcom — <b className="text-ink">Frotcom Bulgaria</b>, Пловдив, бул. „Кукленско
              шосе“ 28 Р, тел. <a className="text-accent underline" href="tel:+35932398964">032 398 964</a>. Поискайте{' '}
              <b className="text-ink">„API V2 credentials“ за нашия профил</b> — отделен потребител и парола за API. Това
              не е входът в сайта на Frotcom.
            </Step>
            <Step n={2}>
              Кажете им какво да позволяват: <b className="text-ink">превозни средства и позиции</b>,{' '}
              <b className="text-ink">километри и гориво</b> („mileage and time“) и по желание шофьори. Попитайте и дали
              има такса за API — Frotcom не я обявява публично.
            </Step>
            <Step n={3}>Въведете получените потребител и парола вдясно и натиснете „Провери връзката“.</Step>
            <Step n={4}>
              „Запиши и свържи бусовете“ — системата сдвоява бусовете по регистрационен номер (кирилица и латиница са
              едно и също). Позициите започват да идват до минута.
            </Step>
          </ol>
        </div>

        <div className="space-y-3">
          {connected && !editing ? (
            <div className="space-y-3">
              <div className="rounded-lg border border-line bg-raised p-3 text-sm">
                <div>
                  Потребител за API: <span className="font-mono">{provider.configuredKeys.username ?? '—'}</span>
                </div>
                <div className="mt-1 text-ink-dim">
                  {provider.lastSyncAt ? `Последно изтегляне ${formatAgo(provider.lastSyncAt)}.` : 'Още няма изтегляне.'}
                </div>
                {failing && provider.lastError ? <div className="mt-2 text-danger">{provider.lastError}</div> : null}
              </div>
              <div className="flex flex-wrap gap-2">
                <Button
                  variant="primary"
                  icon={<Link2 className="size-4" />}
                  loading={link.isPending}
                  onClick={() => link.mutate(provider.id, { onSuccess: (r) => setReport(r) })}
                >
                  Сдвои бусовете отново
                </Button>
                <Button
                  icon={<RefreshCw className="size-4" />}
                  loading={poll.isPending}
                  onClick={() => poll.mutate(undefined, { onSuccess: () => toast.ok('Позициите са изтеглени.') })}
                >
                  Изтегли позициите сега
                </Button>
                <Button icon={<Stethoscope className="size-4" />} onClick={() => setDiagnosing(true)}>
                  Сверка с Frotcom
                </Button>
                <Button variant="ghost" onClick={() => setEditing(true)}>
                  Смени потребителя
                </Button>
              </div>
            </div>
          ) : (
            <form
              className="space-y-3"
              onSubmit={(e) => {
                e.preventDefault();
                test.mutate(
                  { adapter: 'frotcom', config: { username: username.trim(), password } },
                  { onSuccess: (r) => setResult(r) },
                );
              }}
            >
              <Field label="Потребител за API (от Frotcom)" required>
                <Input value={username} onChange={(e) => setUsername(e.target.value)} autoComplete="off" spellCheck={false} />
              </Field>
              <Field label="Парола за API" required hint="Пази се криптирана и не се показва обратно.">
                <Input type="password" value={password} onChange={(e) => setPassword(e.target.value)} autoComplete="new-password" />
              </Field>
              <div className="flex flex-wrap gap-2">
                <Button type="submit" icon={<PlugZap className="size-4" />} loading={test.isPending} disabled={!username.trim() || !password}>
                  Провери връзката
                </Button>
                <Button
                  type="button"
                  variant="primary"
                  disabled={!result?.ok}
                  loading={save.isPending || link.isPending}
                  onClick={() => void saveAndLink().catch(() => undefined)}
                >
                  Запиши и свържи бусовете
                </Button>
                {connected ? (
                  <Button type="button" variant="ghost" onClick={() => setEditing(false)}>
                    Отказ
                  </Button>
                ) : null}
              </div>
              {result ? (
                <div className={cn('rounded-lg border p-3 text-sm', result.ok ? 'border-ok/40 bg-ok-soft' : 'border-danger/40 bg-danger-soft')}>
                  <div className={result.ok ? 'text-ok' : 'text-danger'}>{result.message}</div>
                  {result.ok && result.vehicles.length > 0 ? (
                    <div className="mt-2 flex flex-wrap gap-1.5">
                      {result.vehicles.map((v) => (
                        <span key={v.externalId} className="rounded border border-line bg-surface px-1.5 py-0.5 font-mono text-[11px]">
                          {v.plate ?? v.name}
                        </span>
                      ))}
                    </div>
                  ) : null}
                </div>
              ) : null}
            </form>
          )}

          {status.isLoading && !report ? <p className="text-xs text-ink-dim">Сравнявам бусовете с Frotcom…</p> : null}
          {status.isError && !report ? (
            <p className="text-xs text-danger">Бусовете от Frotcom не се заредиха — натиснете „Сдвои бусовете отново“.</p>
          ) : null}
          {shown ? <LinkResult report={shown} onImport={providerId ? () => setImporting(true) : undefined} /> : null}
          {diagnosing && providerId ? (
            <FrotcomDiagnoseDialog providerId={providerId} open={diagnosing} onClose={() => setDiagnosing(false)} />
          ) : null}
          {importing && shown && providerId ? (
            <ImportVehiclesDialog
              providerId={providerId}
              report={shown}
              onClose={() => setImporting(false)}
              onDone={() => {
                setImporting(false);
                link.mutate(providerId, { onSuccess: (r) => setReport(r) });
              }}
            />
          ) : null}
        </div>
      </div>
    </Card>
  );
}

function Step({ n, children }: { n: number; children: React.ReactNode }) {
  return (
    <li className="flex gap-3">
      <span className="flex size-6 shrink-0 items-center justify-center rounded-full bg-accent-soft font-mono text-xs font-semibold text-accent">
        {n}
      </span>
      <span className="leading-relaxed">{children}</span>
    </li>
  );
}

function LinkResult({ report, onImport }: { report: LinkReport; onImport?: () => void }) {
  return (
    <div className="rounded-lg border border-line p-3 text-sm">
      {onImport && (report.unmatchedTheirs.length > 0 || report.unmatchedOurs.length > 0) ? (
        <div className="mb-3 flex flex-wrap items-center gap-2 rounded-md border border-accent/40 bg-accent-soft p-2.5">
          <span className="min-w-0 flex-1 text-ink">
            {report.unmatchedTheirs.length > 0
              ? `Във Frotcom има ${report.unmatchedTheirs.length} ${report.unmatchedTheirs.length === 1 ? 'бус' : 'буса'}, които ги няма в автопарка.`
              : 'Някои наши бусове ги няма във Frotcom.'}
          </span>
          <Button size="sm" variant="primary" icon={<Truck className="size-4" />} onClick={onImport}>
            Внеси бусовете от Frotcom
          </Button>
        </div>
      ) : null}
      <div className="font-medium">
        Сдвоени: {report.linked.length} {report.linked.length === 1 ? 'бус' : 'буса'}
      </div>
      {report.linked.length > 0 ? (
        <div className="mt-1.5 flex flex-wrap gap-1.5">
          {report.linked.map((v) => (
            <Badge key={v.vehicleId} tone="ok">
              {v.fleetNo} · {v.plate}
            </Badge>
          ))}
        </div>
      ) : null}
      {report.unmatchedOurs.length > 0 ? (
        <div className="mt-3">
          <div className="text-warn">Не са намерени във Frotcom (проверете регистрационния номер):</div>
          <div className="mt-1.5 flex flex-wrap gap-1.5">
            {report.unmatchedOurs.map((v) => (
              <Badge key={v.vehicleId} tone="warn">
                {v.fleetNo} · {v.plate}
              </Badge>
            ))}
          </div>
        </div>
      ) : null}
      {report.unmatchedTheirs.length > 0 ? (
        <div className="mt-3 text-ink-dim">
          Във Frotcom, но не в нашия автопарк: {report.unmatchedTheirs.map((v) => v.plate ?? v.name).join(', ')}
        </div>
      ) : null}
    </div>
  );
}

function TransEuCard() {
  return (
    <Card>
      <CardHeader
        title="Trans.eu — спедиция от нашата платформа"
        subtitle="Проверено в документацията на Trans.eu (септември 2026)."
        actions={<Badge tone="info">възможно частично</Badge>}
      />
      <div className="grid gap-4 p-4 text-sm md:grid-cols-2">
        <div>
          <h3 className="mb-2 font-semibold text-ok">Какво може да се върже</h3>
          <ul className="list-disc space-y-1.5 pl-5 text-ink-dim">
            <li>Товари, изпратени директно на нас от познати спедитори — да ги видим, договорим и приемем оттук.</li>
            <li>Получените транспортни поръчки — с назначаване на бус и шофьор и отчитане на товарене и доставка.</li>
            <li>Обяви за свободни бусове — публикуване и сваляне оттук.</li>
            <li>Когато ние превъзлагаме товар: публикуване, оферти, избор на превозвач.</li>
          </ul>
          <p className="mt-3 text-ink-dim">
            API-то е безплатно при платен акаунт в Trans.eu. Регистрацията на приложението става с вашия TransId на
            trans.eu/api/register-your-app; одобрението отнема около седмица, а всеки диспечер свързва своя акаунт
            веднъж.
          </p>
        </div>
        <div>
          <h3 className="mb-2 inline-flex items-center gap-1.5 font-semibold text-warn">
            <MessageSquareOff className="size-4" /> Какво не може
          </h3>
          <ul className="list-disc space-y-1.5 pl-5 text-ink-dim">
            <li>
              <b className="text-ink">Чатовете (Trans.eu Messenger)</b> — нямат API. Автоматично четене на чатовете е
              забранено от правилата им (глоба до 100 000 PLN и спиране на акаунта).
            </li>
            <li>Търсене в публичната борса и в обявите на други фирми — остава в сайта на Trans.eu.</li>
          </ul>
          <p className="mt-3 text-ink-dim">
            Сделка, договорена в чата, става транспортна поръчка в Trans.eu — тя вече може да влезе тук като заявка.
          </p>
        </div>
      </div>
    </Card>
  );
}
